import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogHeading,
  SubmitButton,
  Typography,
} from 'omakase';
import { ConfirmationHeading } from 'components/business/receipts/ConfirmationHeading';
import { IconNote } from '@tock/icons';
import { OfferingSummaryHeader } from 'components/business/receipts/OfferingSummary/OfferingSummaryHeader';
import { PreVisitQuestionsFields } from '@tock/shared/utils/questionUtils';
import { PreVisitQuestionsFieldset } from 'forms/previsit-questions/PreVisitQuestionsFieldset';
import { QuestionnaireMessage } from 'forms/previsit-questions/QuestionnaireMessage';
import {
  body,
  formContainer,
  formTitle,
  header,
  title,
} from 'components/business/receipts/ReceiptPreVisitQuestionDialog/preVisitQuestionDialogCssDefs';
import { consumer, core } from '@tock/coretypes';
import { isTogoVariety } from '@tock/shared/utils/offerings';
import { useFormikContext } from 'formik';

interface ReceiptPreVisitQuestionDialogFormBodyProps {
  business: consumer.IBusinessConfig;
  isAuthenticated: boolean;
  onClose(): void;
  purchase: core.PastPurchase;
  questions: core.TicketTypeQuestion[];
  questionnaireIntroText?: core.TicketTypeDetails['questionnaireIntroText'];
}

export const ReceiptPreVisitQuestionFormBody = ({
  business,
  onClose,
  questions,
  questionnaireIntroText,
  purchase,
  isAuthenticated,
}: ReceiptPreVisitQuestionDialogFormBodyProps) => {
  const offeringName = isTogoVariety(purchase.ticketType?.variety) ? 'Order' : 'Reservation';
  const { isSubmitting, isValid } = useFormikContext<PreVisitQuestionsFields>();

  return (
    <Dialog
      aria-labelledby="survey-dialog-title"
      aria-describedby="survey-dialog-description"
      open
      data-testid="survey-dialog"
    >
      <DialogForm>
        <DialogHeading css={title} disableTypography>
          <ConfirmationHeading component="h2" id="survey-dialog-title" variant="heading04">
            {offeringName} confirmed
          </ConfirmationHeading>
        </DialogHeading>
        <DialogContent id="survey-dialog-description" disablePadding>
          <div css={header}>
            <OfferingSummaryHeader
              business={business}
              isAuthenticated={isAuthenticated}
              purchase={purchase}
            />
          </div>
          <div css={body}>
            <div css={formContainer}>
              <Typography variant="heading04" css={formTitle}>
                <IconNote /> Pre-visit questions
              </Typography>
              {questionnaireIntroText && (
                <QuestionnaireMessage
                  business={business}
                  message={questionnaireIntroText}
                  data-testid="pre-visit-question-message"
                />
              )}
              <PreVisitQuestionsFieldset questions={questions} />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            size="medium"
            variant="ghost"
            fullWidth
            data-testid="survey-cancel-button"
          >
            Skip for now
          </Button>
          <SubmitButton
            size="medium"
            variant="primary"
            fullWidth
            isSubmitting={isSubmitting}
            disabled={!isValid}
            data-testid="survey-submit-button"
          >
            Submit
          </SubmitButton>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
};
